import { useEffect, useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import { apiRequest } from '../utils/auth';
import Pagination from '../components/Pagination';
import ReportDetailsModal, { ReportType } from '../components/ReportDetailsModal';

const REASON_LABELS: Record<string, string> = {
  inappropriate_content: 'Inappropriate',
  spam: 'Spam',
  copyright_violation: 'Copyright',
  misleading: 'Misleading',
  harassment: 'Harassment',
  other: 'Other',
};

function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] || reason;
}

function formatDate(value?: string): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface ExtensionReportRow {
  extensionId: string;
  extensionExists: boolean;
  name: string;
  isDeleted: boolean;
  reportStatus: string;
  creatorId: string | null;
  creatorName: string | null;
  creatorProfilePicture: string | null;
  thumbnailUrl?: string;
  totalReports: number;
  lastReportedAt: string;
  reasonBreakdown: Record<string, number>;
}

interface CreatorReportRow {
  creatorId: string;
  creatorExists: boolean;
  creatorName: string;
  creatorProfilePicture: string | null;
  isCreatorDisabled: boolean;
  violationType: string | null;
  totalReports: number;
  lastReportedAt: string;
  reasonBreakdown: Record<string, number>;
}

// Colour the report count by severity so heavily-reported rows stand out
function countColor(count: number): string {
  if (count >= 10) return 'var(--color-danger-strong)';
  if (count >= 5) return 'var(--color-warning)';
  return 'var(--color-muted)';
}

function ReportCount({ count }: { count: number }) {
  const color = countColor(count);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 700, fontSize: '15px', color }}>
      {count >= 5 && <FiAlertTriangle size={14} />}
      {count}
    </span>
  );
}

function ReasonChips({ breakdown }: { breakdown: Record<string, number> }) {
  const entries = Object.entries(breakdown).sort((a, b) => b[1] - a[1]);
  return (
    <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
      {entries.map(([reason, count]) => (
        <span key={reason} style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: 'var(--color-surface-hover)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)', fontSize: '11px', whiteSpace: 'nowrap' }}>
          {reasonLabel(reason)} · {count}
        </span>
      ))}
    </span>
  );
}

function ExtStatusBadge({ status, isDeleted }: { status: string; isDeleted: boolean }) {
  let label = status;
  let color = 'var(--color-success-strong)';
  if (status === 'Suspended') { label = 'Suspended'; color = 'var(--color-danger-strong)'; }
  else if (status === 'inReview') { label = 'In Review'; color = 'var(--color-warning-border)'; }
  else if (isDeleted || status === 'Unknown') { label = 'Deleted'; color = 'var(--color-subtle)'; }
  else { label = 'Active'; color = 'var(--color-success-strong)'; }
  return <span style={{ padding: '3px 9px', borderRadius: '6px', backgroundColor: color, color: 'white', fontSize: '11px', fontWeight: 600 }}>{label}</span>;
}

function CreatorStatusBadge({ banned }: { banned: boolean }) {
  return (
    <span style={{ padding: '3px 9px', borderRadius: '6px', backgroundColor: banned ? 'var(--color-danger-strong)' : 'var(--color-success-strong)', color: 'white', fontSize: '11px', fontWeight: 600 }}>
      {banned ? 'Banned' : 'Active'}
    </span>
  );
}

function Thumb({ url, name, rounded }: { url?: string | null; name: string; rounded?: boolean }) {
  if (url) {
    return <img src={url} alt="" style={{ width: 40, height: 40, borderRadius: rounded ? '50%' : '6px', objectFit: 'cover', flexShrink: 0, display: 'block' }} />;
  }
  return (
    <div style={{ width: 40, height: 40, borderRadius: rounded ? '50%' : '6px', backgroundColor: 'var(--color-border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontWeight: 700, fontSize: '16px' }}>
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

const tabBtnBase: React.CSSProperties = {
  padding: '8px 18px',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 600,
};

const thStyle: React.CSSProperties = { padding: '12px', textAlign: 'left', fontWeight: 600, borderBottom: '2px solid var(--color-border)', color: 'var(--color-text)' };

export default function ReportsPage() {
  const [tab, setTab] = useState<ReportType>('extension');

  const [extRows, setExtRows] = useState<ExtensionReportRow[]>([]);
  const [creatorRows, setCreatorRows] = useState<CreatorReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        ...(searchQuery && { search: searchQuery }),
      });
      const base = tab === 'extension' ? '/api/admin/reports/extensions' : '/api/admin/reports/creators';
      const response = await apiRequest<{
        success: boolean;
        data: ExtensionReportRow[] | CreatorReportRow[];
        pagination: PaginationInfo;
        error?: string;
      }>(`${base}?${params}`);
      if (response.success) {
        if (tab === 'extension') {
          setExtRows(response.data as ExtensionReportRow[]);
        } else {
          setCreatorRows(response.data as CreatorReportRow[]);
        }
        setPagination(response.pagination);
      } else {
        setError(response.error || 'Failed to fetch reports');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, currentPage, searchQuery, pageSize]);

  const switchTab = (next: ReportType) => {
    if (next === tab) return;
    setTab(next);
    setCurrentPage(1);
    setSearchQuery('');
    setSearchInput('');
    setPagination(null);
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchQuery(searchInput.trim());
  };

  const openModal = (id: string) => {
    setSelectedId(id);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedId(null);
  };

  return (
    <div className="page">
      <div style={{ marginBottom: '16px' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Reports</h1>
        <p style={{ margin: '6px 0 0', color: 'var(--color-muted)', fontSize: '14px' }}>
          User reports grouped by {tab === 'extension' ? 'lens' : 'creator'}, most reported first.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button
          onClick={() => switchTab('extension')}
          style={{ ...tabBtnBase, backgroundColor: tab === 'extension' ? 'var(--color-accent-strong)' : 'var(--color-surface-hover)', color: tab === 'extension' ? 'white' : 'var(--color-text)', borderColor: tab === 'extension' ? 'var(--color-accent-strong)' : 'var(--color-border)' }}
        >
          Lenses
        </button>
        <button
          onClick={() => switchTab('creator')}
          style={{ ...tabBtnBase, backgroundColor: tab === 'creator' ? 'var(--color-accent-strong)' : 'var(--color-surface-hover)', color: tab === 'creator' ? 'white' : 'var(--color-text)', borderColor: tab === 'creator' ? 'var(--color-accent-strong)' : 'var(--color-border)' }}
        >
          Creators
        </button>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: '10px', marginBottom: '16px', maxWidth: '480px' }}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={tab === 'extension' ? 'Search by lens or creator name…' : 'Search by creator name…'}
          style={{ flex: 1, minWidth: 0, padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: '4px', fontSize: '14px', backgroundColor: 'var(--color-input)', color: 'var(--color-text)' }}
        />
        <button type="submit" style={{ padding: '8px 16px', backgroundColor: 'var(--color-accent-strong)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
          Search
        </button>
      </form>

      {error && (
        <div style={{ padding: '12px', backgroundColor: 'var(--color-danger-surface)', color: 'var(--color-danger)', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {pagination && (
        <div style={{ marginBottom: '16px', color: 'var(--color-muted)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span>Showing {tab === 'extension' ? extRows.length : creatorRows.length} of {pagination.total} reported {tab === 'extension' ? 'lenses' : 'creators'}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <label htmlFor="reports-page-size">Per page:</label>
            <select
              id="reports-page-size"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              style={{ padding: '4px 8px', border: '1px solid var(--color-border)', borderRadius: '4px', backgroundColor: 'var(--color-input)', color: 'var(--color-text)', fontSize: '13px' }}
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-muted)' }}>Loading reports…</div>
      ) : tab === 'extension' ? (
        extRows.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-subtle)' }}>No lens reports found.</div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {extRows.map((row) => (
              <div key={row.extensionId} onClick={() => openModal(row.extensionId)} style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Thumb url={row.thumbnailUrl} name={row.name} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: 'var(--color-text)', fontSize: '15px' }}>{row.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--color-subtle)' }}>{row.creatorName || '-'}</div>
                  </div>
                  <ReportCount count={row.totalReports} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                  <ReasonChips breakdown={row.reasonBreakdown} />
                  <ExtStatusBadge status={row.reportStatus} isDeleted={row.isDeleted} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflowX: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: 'var(--color-surface-hover)' }}>
                <tr>
                  <th style={{ ...thStyle, width: '52px' }}></th>
                  <th style={thStyle}>Lens</th>
                  <th style={thStyle}>Creator</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Reports</th>
                  <th style={thStyle}>Reasons</th>
                  <th style={thStyle}>Last report</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {extRows.map((row) => (
                  <tr
                    key={row.extensionId}
                    onClick={() => openModal(row.extensionId)}
                    style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td style={{ padding: '12px', width: '52px' }}><Thumb url={row.thumbnailUrl} name={row.name} /></td>
                    <td style={{ padding: '12px', fontWeight: 500, color: 'var(--color-text)' }}>{row.name}</td>
                    <td style={{ padding: '12px', color: 'var(--color-muted)', fontSize: '14px' }}>{row.creatorName || '-'}</td>
                    <td style={{ padding: '12px', textAlign: 'right' }}><ReportCount count={row.totalReports} /></td>
                    <td style={{ padding: '12px' }}><ReasonChips breakdown={row.reasonBreakdown} /></td>
                    <td style={{ padding: '12px', color: 'var(--color-muted)', fontSize: '13px' }}>{formatDate(row.lastReportedAt)}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}><ExtStatusBadge status={row.reportStatus} isDeleted={row.isDeleted} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : creatorRows.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-subtle)' }}>No creator reports found.</div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {creatorRows.map((row) => (
            <div key={row.creatorId} onClick={() => openModal(row.creatorId)} style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Thumb url={row.creatorProfilePicture} name={row.creatorName} rounded />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: 'var(--color-text)', fontSize: '15px' }}>{row.creatorName}</div>
                </div>
                <ReportCount count={row.totalReports} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                <ReasonChips breakdown={row.reasonBreakdown} />
                <CreatorStatusBadge banned={row.isCreatorDisabled} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', overflowX: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: 'var(--color-surface-hover)' }}>
              <tr>
                <th style={{ ...thStyle, width: '52px' }}></th>
                <th style={thStyle}>Creator</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Reports</th>
                <th style={thStyle}>Reasons</th>
                <th style={thStyle}>Last report</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {creatorRows.map((row) => (
                <tr
                  key={row.creatorId}
                  onClick={() => openModal(row.creatorId)}
                  style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <td style={{ padding: '12px', width: '52px' }}><Thumb url={row.creatorProfilePicture} name={row.creatorName} rounded /></td>
                  <td style={{ padding: '12px', fontWeight: 500, color: 'var(--color-text)' }}>{row.creatorName}</td>
                  <td style={{ padding: '12px', textAlign: 'right' }}><ReportCount count={row.totalReports} /></td>
                  <td style={{ padding: '12px' }}><ReasonChips breakdown={row.reasonBreakdown} /></td>
                  <td style={{ padding: '12px', color: 'var(--color-muted)', fontSize: '13px' }}>{formatDate(row.lastReportedAt)}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}><CreatorStatusBadge banned={row.isCreatorDisabled} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && (
        <Pagination currentPage={currentPage} totalPages={pagination.pages} onPageChange={setCurrentPage} />
      )}

      {modalOpen && selectedId && (
        <ReportDetailsModal
          type={tab}
          id={selectedId}
          isOpen={modalOpen}
          onClose={closeModal}
          onActionDone={fetchReports}
        />
      )}
    </div>
  );
}
